import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { usePermissions } from 'react-admin';
import { Box, Typography } from '@mui/material';
import type { Permissions } from '../types';
import { can } from '../utils/capability-helpers';
import { CommandDashboard } from './CommandDashboard';
import { CadreDashboard } from './CadreDashboard';
import { AnalystDashboard } from './AnalystDashboard';
import { canonicalOrgPath } from '../hooks/useRollup';

const COMMAND_ROLES = new Set(['tenant_admin', 'enterprise_leader', 'echelon_commander']);
const CADRE_ROLES   = new Set(['cadre_manager']);
const ANALYST_ROLES = new Set(['analyst']);

type DashboardKind = 'command' | 'cadre' | 'analyst';

export interface AnalyticsScope {
  kind: DashboardKind;
  role: string;
  orgPath: string;
  key: string;
}

function highestRole(permissions: Permissions | undefined): DashboardKind | null {
  const assignments = permissions?.assignments;
  if (!assignments?.length) return null;
  const roles = assignments.map((p) => p.role);
  if (roles.some((r) => COMMAND_ROLES.has(r))) return 'command';
  if (roles.some((r) => CADRE_ROLES.has(r)))   return 'cadre';
  if (roles.some((r) => ANALYST_ROLES.has(r)))  return 'analyst';
  return null;
}

function rolesForDashboard(kind: DashboardKind): Set<string> {
  if (kind === 'command') return COMMAND_ROLES;
  if (kind === 'cadre') return CADRE_ROLES;
  return ANALYST_ROLES;
}

function getAnalyticsScope(
  permissions: Permissions | undefined,
): AnalyticsScope | null {
  if (!can(permissions, 'view_analytics')) return null;
  const kind = highestRole(permissions);
  if (!kind) return null;
  const roles = rolesForDashboard(kind);
  const assignments = permissions?.assignments
    .filter((assignment) => roles.has(assignment.role))
    .filter((assignment) => canonicalOrgPath(assignment.orgPath) === assignment.orgPath)
    .sort((left, right) =>
      left.orgPath.length - right.orgPath.length || left.role.localeCompare(right.role));
  const assignment = assignments?.[0];
  if (!assignment) return null;
  return {
    kind,
    role: assignment.role,
    orgPath: assignment.orgPath,
    key: `${assignment.role}:${assignment.orgPath}`,
  };
}

const AccessDenied = () => (
  <Box p={4}>
    <Typography variant="h6" gutterBottom>
      Access Denied
    </Typography>
    <Typography color="text.secondary">
      No analytics dashboard is assigned for your current organization and role.
    </Typography>
  </Box>
);

export function AnalyticsBoundary({
  children,
}: {
  children: ReactNode | ((scope: AnalyticsScope) => ReactNode);
}) {
  const { permissions, isLoading } = usePermissions<Permissions>();

  if (isLoading) return null;
  const scope = getAnalyticsScope(permissions);
  if (!scope) return <AccessDenied />;
  const content = typeof children === 'function' ? children(scope) : children;
  return <Fragment key={scope.key}>{content}</Fragment>;
}

const Dashboard = () => {
  return (
    <AnalyticsBoundary>
      {(scope) => {
        if (scope.kind === 'command') {
          return <CommandDashboard rootOrgPath={scope.orgPath} />;
        }
        if (scope.kind === 'cadre') {
          return <CadreDashboard orgPath={scope.orgPath} />;
        }
        return <AnalystDashboard defaultOrgPath={scope.orgPath} />;
      }}
    </AnalyticsBoundary>
  );
};

export default Dashboard;
